import { Box, Typography } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { makeStyles } from "@mui/styles";
import React, { useEffect } from "react";

import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { BASE_URL } from "../../../static/constants";
import formatDate from "../../../utils/formatDate";
import getToken from "../../../utils/getToken";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#282828",
    color: "#fff",
  },
  tooltipVisible: {
    "& .recharts-tooltip-wrapper": {
      visibility: "visible !important",
    },
  },
}));

const Rechart = (props) => {
  const classes = useStyles();
  const { patientId } = props;

  const [fullData, setFullData] = React.useState(null);
  const [chartData, setChartData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [lastSystolic, setLastSystolic] = React.useState(null);
  const [lastSystolicX, setLastSystolicX] = React.useState(null);
  const [lastSystolicY, setLastSystolicY] = React.useState(null);
  const [lastDiastolic, setLastDiastolic] = React.useState(null);
  const [lastDiastolicX, setLastDiastolicX] = React.useState(null);
  const [lastDiastolicY, setLastDiastolicY] = React.useState(null);

  useEffect(() => {
    fetch(`${BASE_URL}/patient/rpm/btbp/${patientId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.error) {
          throw Error(data.message);
        } else {
          const chart = generateDataObject(data.data);
          setFullData(data.data);
          setChartData(chart);
          setLoading(false);
        }
      })
      .catch((error) => {
        const errorStr = error.toString();
        if (errorStr === "Error: jwt malformed") {
          this.props.navigate("/login");
        }
        if (errorStr === "Error: jwt expired") {
          this.props.navigate("/login");
        }
        if (errorStr === "Error: Unauthorized") {
          this.props.navigate("/prohibited");
        }
      });
    // eslint-disable-next-line
  }, []);

  const getDiastolicLines = (data) => {
    const { plottedData } = data.diastolic;

    const lineData = plottedData.map((p) => {
      const formattedDate = formatIsoDate(p.timestamp);

      return {
        value: formatDate(formattedDate),
        DiastolicLine: p.value,
      };
    });

    return lineData;
  };

  const formatIsoDate = (date) => {
    const nDate = new Date(date);
    return `${nDate.getMonth() + 1}/${nDate.getDate()}/${nDate.getFullYear()}`;
  };

  const getSystolicLines = (data) => {
    const { plottedData } = data.systolic;

    const lineData = plottedData.map((p) => {
      const formattedDate = formatIsoDate(p.timestamp);

      return {
        value: formatDate(formattedDate),
        SystolicLine: p.value,
      };
    });

    return lineData;
  };

  const getscatterDiastolic = (data) => {
    const { allData } = data.diastolic;

    const scatterData = allData.map((p) => {
      const formattedDate = formatIsoDate(p.timestamp);
      return {
        value: formatDate(formattedDate),
        Diastolic: p.value,
      };
    });

    return scatterData;
  };

  const getscatterSystolic = (data) => {
    const { allData } = data.systolic;

    const scatterData = allData.map((p) => {
      const formattedDate = formatIsoDate(p.timestamp);
      return {
        value: formatDate(formattedDate),
        Systolic: p.value,
      };
    });

    return scatterData;
  };

  const getScatterPoints = (data) => {
    const scatterDiastolic = getscatterDiastolic(data);
    const scatterSystolic = getscatterSystolic(data);

    const scatterData = scatterDiastolic.map((d, i) => {
      if (d.value === scatterSystolic[i].value) {
        return {
          value: d.value,
          Diastolic: d.Diastolic,
          Systolic: scatterSystolic[i].Systolic,
        };
      }

      return null;
    });

    return scatterData;
  };

  const generateDataObject = (data) => {
    const diastolicLines = getDiastolicLines(data);
    const systolicLines = getSystolicLines(data);
    const scatterPoints = getScatterPoints(data);

    return [
      ...new Set([...scatterPoints, ...diastolicLines, ...systolicLines]),
    ];
  };

  if (loading || !chartData) {
    return null;
  }

  const getTooltipData = (label) => {
    let relativeDAll = fullData.diastolic.allData.filter((item) => {
      return formatDate(formatIsoDate(item.timestamp)) === formatDate(label);
    });

    let relativeSAll = fullData.systolic.allData.filter((item) => {
      return formatDate(formatIsoDate(item.timestamp)) === formatDate(label);
    });

    let relativeHRAll = fullData.hr.filter((item) => {
      return formatDate(formatIsoDate(item.timestamp)) === formatDate(label);
    });

    const mergedAll = relativeDAll.map((dItem) => {
      const match = relativeSAll.find((sitem) => {
        return dItem.timestamp === sitem.timestamp;
      });

      const matchHR = relativeHRAll.find((hritem) => {
        return dItem.timestamp === hritem.timestamp;
      });

      const obj = {
        dValue: dItem.value,
        sValue: match.value,
        timestamp: dItem.timestamp,
        hr: matchHR.value,
      };

      return obj;
    });

    const sortedData = mergedAll.sort(function (x, y) {
      return new Date(y.timestamp) - new Date(x.timestamp);
    });

    return sortedData;
  };

  const CustomizedDot = (props) => {
    const { cx, cy, dataKey } = props;
    const lastSystolicIndex = fullData.systolic.plottedData.length;
    const lastSystolicValue =
      fullData.systolic.plottedData[lastSystolicIndex - 1].value;

    const lastDiastolicIndex = fullData.diastolic.plottedData.length;
    const lastDiastolicValue =
      fullData.diastolic.plottedData[lastDiastolicIndex - 1].value;

    if (dataKey === "SystolicLine") {
      setLastSystolic(lastSystolicValue);
      setLastSystolicX(parseInt(cx + 20));
      setLastSystolicY(parseInt(cy + 100));
    }

    if (dataKey === "DiastolicLine") {
      setLastDiastolic(lastDiastolicValue);
      setLastDiastolicX(parseInt(cx + 20));
      setLastDiastolicY(cy + 120 + 225);
    }

    return null;

    // if (!points[index].y) {
    //   return null;
    // } else {
    //   return (
    //     <circle cx={cx} cy={cy} r={3} stroke="red" strokeWidth={2} fill="red" className="last_dot" />
    //   );
    // }
  };

  const CustomTooltip = (props) => {
    if (props.active) {
      const data = getTooltipData(props.label);
      return (
        <div style={{ background: "white", color: "black", padding: 10 }}>
          <div style={{ fontWeight: "bold", marginBottom: 10, fontSize: 18 }}>
            {formatDate(props.label)}
          </div>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>BP (mmHg)</TableCell>
                  <TableCell>HR (bpm)</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell
                      style={index === 0 ? { fontWeight: "bold" } : null}
                    >{`${row.sValue}/${row.dValue}`}</TableCell>
                    <TableCell
                      style={index === 0 ? { fontWeight: "bold" } : null}
                    >
                      {row.hr}
                    </TableCell>
                    <TableCell
                      style={index === 0 ? { fontWeight: "bold" } : null}
                    >
                      {new Date(row.timestamp).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      );
    } else {
      return null;
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <Box className={classes.root}>
        <Box mb="2em" p="2em">
          <Typography variant="h4">Blood Pressure</Typography>
        </Box>
        <ResponsiveContainer width="100%" height="100%" aspect={4}>
          <ComposedChart
            width={500}
            height={400}
            data={chartData}
            margin={{
              top: 20,
              right: 80,
              bottom: 20,
              left: 20,
            }}
          >
            <CartesianGrid stroke="#eee" vertical={false} />
            <Tooltip content={<CustomTooltip />} filterNull={false} />

            <Legend
              wrapperStyle={{
                bottom: "0 !important",
              }}
            />

            <XAxis
              dataKey="value"
              type="category"
              allowDuplicatedCategory={false}
              label={{
                value: "",
                position: "insideBottomLeft",
                offset: 0,
                fill: "#fff",
              }}
              tick={{ fill: "white" }}
            />
            <YAxis
              domain={[0, 225]}
              fill="fff"
              type="number"
              label={{
                value: "mmHg",
                angle: -90,
                position: "insideLeft",
                fill: "#fff",
                extraData: {
                  data: { hello: "world" },
                },
              }}
              tick={{ fill: "white" }}
            />
            <Scatter name="Systolic" dataKey="Systolic" fill="#0096bd" />
            <Scatter name="Diastolic" dataKey="Diastolic" fill="#f09000" />
            <Line
              dataKey="SystolicLine"
              stroke="#0096bd"
              dot={<CustomizedDot />}
              activeDot={false}
              legendType="none"
              strokeWidth={3}
            />
            <Line
              dataKey="DiastolicLine"
              stroke="#f09000"
              dot={<CustomizedDot />}
              activeDot={false}
              legendType="none"
              strokeWidth={3}
            />
          </ComposedChart>
        </ResponsiveContainer>

        <div
          style={{
            position: "absolute",
            top: lastSystolicY,
            left: lastSystolicX,
            color: "#fff",
            fontWeight: "bold",
            fontSize: 26,
          }}
        >
          {lastSystolic}
        </div>

        <div
          style={{
            position: "absolute",
            top: lastDiastolicY,
            left: lastDiastolicX,
            color: "#fff",
            fontWeight: "bold",
            fontSize: 26,
          }}
        >
          {lastDiastolic}
        </div>

        <Box display="flex" flexWrap="wrap" justifyContent="flex-end">
          <Box m="1em">
            <Typography>
              <div style={{ fontWeight: "bold", fontSize: 20 }}>
                Mean:{" "}
                {`${fullData.systolic.mean.value}/${fullData.diastolic.mean.value}`}
              </div>
            </Typography>
          </Box>
          <Box m="1em">
            <Typography>
              <div style={{ fontSize: 20 }}>
                <strong>Min:</strong>{" "}
                <strong>{fullData.systolic.min.value}</strong>/
                {fullData.systolic.min.corrDia} {fullData.diastolic.min.corrSys}
                /<strong>{fullData.diastolic.min.value}</strong>
              </div>
            </Typography>
          </Box>
          <Box m="1em">
            <Typography>
              <div style={{ fontSize: 20 }}>
                <strong>Max:</strong>{" "}
                <strong>{fullData.systolic.max.value}</strong>/
                {fullData.systolic.max.corrDia} {fullData.diastolic.max.corrSys}
                /<strong>{fullData.diastolic.max.value}</strong>
              </div>
            </Typography>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default Rechart;
